import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchWikiTree } from '../api/client'
import { Library, Loader2, AlertCircle } from 'lucide-react'
import type { WikiTree } from '../types/wiki'

export default function ShelfHomePage() {
  const [wikiTree, setWikiTree] = useState<WikiTree | null>(null)
  const [wikiAvailable, setWikiAvailable] = useState<boolean | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const load = useCallback(async () => {
    try {
      const tree = await fetchWikiTree()
      setWikiTree(tree)
      setWikiAvailable(true)
    } catch {
      setWikiTree(null)
      setWikiAvailable(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load, reloadKey])

  return (
    <div className="w-full max-w-[min(100%,72rem)] mx-auto px-4 lg:px-10 py-8">
      <h1 className="text-3xl font-bold text-gray-100 mb-2">FairCorp Master Docs</h1>
      <p className="text-gray-400 mb-8">
        Browse the internal archive — shelves, books, and pages.
      </p>

      {wikiAvailable === null && (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Loading wiki…
        </div>
      )}

      {wikiAvailable && wikiTree && wikiTree.shelves.length > 0 && (
        <div className="mb-10">
          <h2 className="text-lg font-semibold text-gray-200 mb-4">Shelves</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {wikiTree.shelves.map((shelf) => (
              <Link
                key={shelf.id}
                to={`/s/${shelf.slug}`}
                className="rounded-xl border border-gray-700/60 bg-gray-900/40 p-5 hover:border-primary-700/50 hover:bg-gray-900/70 transition-colors min-h-[44px]"
              >
                <div className="flex items-start gap-3">
                  <Library className="w-6 h-6 text-primary-400 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-100 truncate">{shelf.name}</h3>
                    {shelf.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{shelf.description}</p>
                    )}
                    <p className="text-xs text-gray-600 mt-2">
                      {shelf.books.length} book{shelf.books.length === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {wikiAvailable && wikiTree && wikiTree.shelves.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500 border border-dashed border-gray-800 rounded-xl">
          <Library className="w-10 h-10 mb-2 opacity-40" />
          <p className="text-sm">No shelves exist yet.</p>
        </div>
      )}

      {wikiAvailable === false && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500 border border-dashed border-gray-800 rounded-xl">
          <AlertCircle className="w-10 h-10 mb-2 opacity-40" />
          <p className="text-sm">The wiki archive is currently unavailable.</p>
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            className="mt-4 text-sm font-medium rounded-lg px-4 py-2 min-h-[44px] bg-gray-800/90 border border-gray-700 text-gray-200 hover:bg-gray-800"
          >
            Try again
          </button>
        </div>
      )}
    </div>
  )
}
